import { ChangeDetectionStrategy, Component, computed, inject, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { RowActions, ValidationHistoryActions } from '../location-list/state/location-list-actions';
import { selectFocusedLocation, selectValidationHistory, selectValidationHistoryLoading } from '../location-list/state/location-list-selectors';
import { ColDef, GetRowIdParams, GridOptions, GridReadyEvent, GridSizeChangedEvent, RowDataUpdatedEvent, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { CoordinateHistory, CoordinateSource } from '../../../models/coordinate-history';
import { DEFAULT_GRID_COLUMN_DEFS, DEFAULT_GRID_OPTIONS } from '../constants';
import { AgBasicHeaderComponent } from '../../global/gx-ag-grid/ag-basic-header/ag-basic-header.component';
import { AgMultiSelectFilterComponent } from '../../global/gx-ag-grid/ag-multi-select-filter/ag-multi-select-filter.component';
import { AgListFloatingFilterComponent } from '../../global/gx-ag-grid/ag-list-floating-filter/ag-list-floating-filter.component';
import { getEnumValues } from '../../../core/utilities';
import { TranslateService } from '@ngx-translate/core';
import { LocationListApiService } from '../location-list/location-list-api.service';
import { AgGridAngular } from 'ag-grid-angular';
import { JumperComponent } from '../../global/jumper/jumper.component';
import { MatProgressBar } from '@angular/material/progress-bar';
import { LocationListLocalStoreService } from '../location-list/location-list-local-store.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { AgDateTimeFilterComponent } from '../../global/gx-ag-grid/ag-date-time-filter/ag-date-time-filter.component';
import { AgDateTimeFloatingFilterComponent } from '../../global/gx-ag-grid/ag-date-time-floating-filter/ag-date-time-floating-filter.component';
import { formatLocalizedDate } from '../../../core/utils/date-fns-utils';
import { TypeCellComponent } from './type-cell/type-cell.component';

@Component({
  selector: 'app-validation-history',
  imports: [
    AgGridAngular,
    JumperComponent,
    MatProgressBar,
  ],
  templateUrl: './validation-history.component.html',
  styleUrls: ['./validation-history.component.scss', '../../locations-skeleton/bls-updates/bls-updates.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ValidationHistoryComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly locationListApi = inject(LocationListApiService);
  private readonly locationListLocalStore = inject(LocationListLocalStoreService);

  protected readonly validationHistory = this.store.selectSignal(selectValidationHistory);
  protected readonly validationHistoryLoading = this.store.selectSignal(selectValidationHistoryLoading);
  protected readonly focusedLocation = this.store.selectSignal(selectFocusedLocation);
  protected readonly locationTotal = toSignal(this.locationListLocalStore.total$);
  protected readonly currentRowOneBasedIndex = computed(() => {
    const focusedLocation = this.focusedLocation();

    if (!focusedLocation) {
      return undefined;
    }

    return this.locationListApi.getRowById(focusedLocation.id.toString())?.rowIndex + 1;
  });

  protected readonly defaultColumnDefs: ColDef<CoordinateHistory> = {
    ...DEFAULT_GRID_COLUMN_DEFS,
    resizable: false,
    autoHeight: true,
    wrapText: true,
    suppressMovable: true,
  }

  protected readonly gridOptions: GridOptions = {
    ...DEFAULT_GRID_OPTIONS,
    suppressCellFocus: true,
    getRowId: this.getRowId.bind(this),
  };

  protected readonly columnDefs: ColDef<CoordinateHistory>[] = [
    {
      headerName: 'Type',
      headerComponent: AgBasicHeaderComponent,
      field: 'source',
      sortable: true,
      filter: AgMultiSelectFilterComponent,
      filterParams: {
        filterOptions: getEnumValues(CoordinateSource).map((val) => ({
          name: this.translate.instant(`COORDINATES.${val}`),
          value: val
        }))
      },
      floatingFilter: true,
      floatingFilterComponent: AgListFloatingFilterComponent,
      cellRenderer: TypeCellComponent,
      cellDataType: 'text',
    },
    {
      headerName: 'Coordinates',
      headerComponent: AgBasicHeaderComponent,
      field: 'coordinates',
      floatingFilterComponent: AgListFloatingFilterComponent,
      cellDataType: 'text',
      valueGetter: (params: ValueGetterParams<CoordinateHistory>) => {
        let str = '';
        if (params.data?.coordinates?.latitude) str += params.data?.coordinates?.latitude + ',\n';
        if (params.data?.coordinates?.longitude) str += params.data?.coordinates?.longitude;
        return str;
      },
    },
    {
      headerName: 'Time',
      headerComponent: AgBasicHeaderComponent,
      headerComponentParams: { enableFilterButton: false },
      field: 'createdDateTime',
      filter: AgDateTimeFilterComponent,
      floatingFilter: true,
      floatingFilterComponent: AgDateTimeFloatingFilterComponent,
      cellDataType: 'text',
      valueGetter: (params: ValueGetterParams<CoordinateHistory>) => {
        return params.data?.createdDateTime ? new Date(params.data.createdDateTime) : null;
      },
      valueFormatter: (params: ValueFormatterParams<CoordinateHistory>) => {
        return params.value ? formatLocalizedDate(params.value, 'dd.MM.yyyy, HH:mm') : '';
      },
    },
  ];

  ngOnInit() {
    const location = this.store.selectSignal(selectFocusedLocation);
    // get validation history on init
    if (location()?.id) {
      this.store.dispatch(ValidationHistoryActions.getValidationHistory({
        locationId: location().id
      }));
    }
  }

  onGridReady(e: GridReadyEvent<CoordinateHistory>) {
    e.api.sizeColumnsToFit();
  }

  onGridSizeChanged(e: GridSizeChangedEvent<CoordinateHistory>) {
    e.api.sizeColumnsToFit();
  }

  onRowDataUpdated(e: RowDataUpdatedEvent<CoordinateHistory>) {
    e.api.sizeColumnsToFit();
  }

  /**
   * Change the data source for this component and scroll data row into view
   * @param nextRowOneBasedIndex
   */
  changeCurrentRow(nextRowOneBasedIndex: number) {
    const nextFocusedRow = this.locationListApi.getRowByIndex(nextRowOneBasedIndex - 1);
    this.store.dispatch(RowActions.updateFocusedRow({ id: Number(nextFocusedRow.id) }));
    this.locationListApi.scrollRowToView(nextFocusedRow);
  };

  /**
   * Needed since we're using immutable data (ngrx)
   * https://www.ag-grid.com/angular-data-grid/data-update-row-data/
   * https://stackoverflow.com/questions/58879648/prevent-ag-grid-from-re-rendering-when-data-ngrx-store-changes
   * @param params
   */
  private getRowId(params: GetRowIdParams<CoordinateHistory>): string {
    return String(params.data.id);
  }
}
